import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { AlertTriangle, Clock, Eye, ShieldAlert, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useAccountStatus } from '@/hooks/queries/useAccountStatus';
import {
  expiryBannerDismissKey,
  persistDismissed,
  readDismissed,
} from '@/lib/billing/bannerDismiss';
import {
  derivePhase,
  graceProgressPercent,
  type ExpiryPhase,
  type WarningTone,
} from '@/lib/billing/phase';

const HARD_GRACE_DAYS = 14;

type Severity = 'amber-soft' | 'amber-strong' | 'terracotta' | 'error-soft' | 'error' | 'readonly';

interface SeverityStyles {
  container: string;
  icon: React.ComponentType<{ className?: string }>;
  iconWrap: string;
  countdown: string;
  primaryBtn: string;
  ghostBtn: string;
  progressTrack: string;
  progressFill: string;
}

const STYLES: Record<Severity, SeverityStyles> = {
  'amber-soft': {
    container: 'bg-warning-light text-charcoal-900 border-warning/40',
    icon: Clock,
    iconWrap: 'bg-warning text-charcoal-950',
    countdown: 'bg-charcoal-950 text-white',
    primaryBtn: 'bg-lime-500 text-charcoal-950 hover:bg-lime-400',
    ghostBtn: 'text-charcoal-700 hover:text-charcoal-950',
    progressTrack: 'bg-warning/30',
    progressFill: 'bg-charcoal-950',
  },
  'amber-strong': {
    container: 'bg-warning text-charcoal-950 border-warning-dark/40',
    icon: AlertTriangle,
    iconWrap: 'bg-white text-charcoal-950',
    countdown: 'bg-charcoal-950 text-white',
    primaryBtn: 'bg-lime-500 text-charcoal-950 hover:bg-lime-400',
    ghostBtn: 'text-charcoal-800 hover:text-charcoal-950',
    progressTrack: 'bg-charcoal-950/20',
    progressFill: 'bg-charcoal-950',
  },
  terracotta: {
    container: 'bg-terracotta-500 text-white border-terracotta-600/60',
    icon: AlertTriangle,
    iconWrap: 'bg-white text-terracotta-500',
    countdown: 'bg-white text-charcoal-950',
    primaryBtn: 'bg-white text-charcoal-950 hover:bg-charcoal-50',
    ghostBtn: 'text-white hover:text-warm-100',
    progressTrack: 'bg-white/30',
    progressFill: 'bg-white',
  },
  'error-soft': {
    container: 'bg-error-light text-charcoal-900 border-l-[6px] border-l-error border-error/40',
    icon: ShieldAlert,
    iconWrap: 'bg-error text-white',
    countdown: 'bg-error text-white',
    primaryBtn: 'bg-lime-500 text-charcoal-950 hover:bg-lime-400',
    ghostBtn: 'text-charcoal-700 hover:text-charcoal-950',
    progressTrack: 'bg-error/20',
    progressFill: 'bg-error',
  },
  error: {
    container: 'bg-error text-white border-error-dark/60',
    icon: ShieldAlert,
    iconWrap: 'bg-charcoal-950 text-white',
    countdown: 'bg-white text-error',
    primaryBtn: 'bg-white text-error hover:bg-warm-100',
    ghostBtn: 'text-white hover:text-warm-100',
    progressTrack: 'bg-white/25',
    progressFill: 'bg-white',
  },
  readonly: {
    container: 'bg-charcoal-950 text-white border-charcoal-700',
    icon: Eye,
    iconWrap: 'bg-lime-500 text-charcoal-950',
    countdown: 'bg-lime-500 text-charcoal-950',
    primaryBtn: 'bg-lime-500 text-charcoal-950 hover:bg-lime-400',
    ghostBtn: 'text-white hover:text-warm-100',
    progressTrack: 'bg-white/15',
    progressFill: 'bg-lime-500',
  },
};

function severityFor(
  phase: ExpiryPhase,
  warningTone: WarningTone | null,
  graceDaysLeft: number,
  graceHardDays: number = HARD_GRACE_DAYS,
): Severity {
  if (phase === 'reconversion') return 'readonly';
  if (phase === 'warning') {
    if (warningTone === 'urgent') return 'terracotta';
    if (warningTone === 'medium') return 'amber-strong';
    return 'amber-soft';
  }
  if (phase === 'grace') {
    const halfway = graceHardDays / 2;
    return graceDaysLeft <= halfway ? 'error' : 'error-soft';
  }
  return 'amber-soft';
}

interface ExpiryCountdownBannerProps {
  /** Optional override for the storage key prefix used by dismiss memory. */
  storageKey?: string;
}

export function ExpiryCountdownBanner({
  storageKey = 'velko.expiry.dismissed',
}: ExpiryCountdownBannerProps = {}) {
  const { t } = useTranslation('billing');
  const { data } = useAccountStatus();
  const location = useLocation();
  const [dismissed, setDismissed] = useState<string[]>([]);

  useEffect(() => {
    setDismissed(readDismissed(storageKey));
  }, [storageKey]);

  const dismiss = useCallback(
    (key: string) => {
      setDismissed((prev) => {
        const next = Array.from(new Set([...prev, key]));
        persistDismissed(storageKey, next);
        return next;
      });
    },
    [storageKey],
  );

  if (!data) return null;

  const { phase, warningTone, daysLabel } = derivePhase(
    data.access_state,
    data.days_to_expiry,
    data.grace_days_left,
    data.trial_days_left,
    data.write_restricted,
  );

  if (phase === 'silent' || phase === 'reconversion') return null;

  // Hide banner on subscription pages to avoid stacking with native UI
  if (location.pathname.startsWith('/subscription')) return null;

  const severity = severityFor(phase, warningTone, data.grace_days_left);
  const styles = STYLES[severity];
  const Icon = styles.icon;
  const isWarning = phase === 'warning';
  const isTrial = data.access_state === 'trial_active';
  const dismissable = isWarning && warningTone === 'soft';
  const dismissKey = expiryBannerDismissKey({
    phase,
    accessState: data.access_state,
    severity,
    daysToExpiry: data.days_to_expiry,
    trialDaysLeft: data.trial_days_left,
    daysSinceExpiry: data.days_since_expiry,
  });

  if (dismissable && dismissed.includes(dismissKey)) return null;

  const progressPercent = phase === 'grace' ? graceProgressPercent(data.grace_days_left) : null;

  let title: string;
  let message: string;
  let countdownLabel = daysLabel;

  if (isWarning) {
    if (warningTone === 'urgent') {
      title = isTrial ? t('expiry_trial_warning_urgent_title') : t('expiry_warning_urgent_title');
      message = isTrial
        ? t('expiry_trial_warning_urgent_message')
        : t('expiry_warning_urgent_message');
      countdownLabel = '24 h';
    } else if (warningTone === 'medium') {
      title = isTrial ? t('expiry_trial_warning_medium_title') : t('expiry_warning_medium_title');
      message = isTrial
        ? t('expiry_trial_warning_medium_message')
        : t('expiry_warning_medium_message');
    } else {
      title = isTrial ? t('expiry_trial_warning_soft_title') : t('expiry_warning_soft_title');
      message = isTrial ? t('expiry_trial_warning_soft_message') : t('expiry_warning_soft_message');
    }
  } else {
    // grace
    title = t('expiry_grace_title');
    message = t('expiry_grace_message', { days: data.grace_days_left });
  }

  const cta = isTrial ? '/subscription/plans' : '/subscription';

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'relative flex items-center gap-3 border-b-2 border-charcoal-950 px-4 py-3 sm:px-6',
        styles.container,
      )}
    >
      <div
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-md border-2 border-charcoal-950',
          styles.iconWrap,
        )}
      >
        <Icon className="h-4 w-4" />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-bold leading-tight font-heading">{title}</p>
          {countdownLabel && (
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-bold font-mono',
                styles.countdown,
              )}
            >
              {countdownLabel}
            </span>
          )}
        </div>
        <p className="mt-0.5 text-xs sm:text-sm opacity-95">{message}</p>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <Link
          to={cta}
          className={cn(
            'inline-flex items-center justify-center rounded-md border-2 border-charcoal-950 px-3 py-2 text-xs font-bold font-heading shadow-[2px_2px_0_#0d0d1a] transition-transform hover:-translate-x-px hover:-translate-y-px sm:text-sm',
            styles.primaryBtn,
          )}
        >
          {phase === 'grace' ? t('expiry_cta_renew_now') : t('expiry_cta_renew')}
        </Link>

        {!isWarning && (
          <Link
            to="/subscription/plans"
            className={cn(
              'hidden md:inline-flex items-center justify-center rounded-md border-2 px-3 py-2 text-xs font-bold font-heading sm:text-sm',
              styles.ghostBtn,
              'border-current',
            )}
          >
            {t('expiry_cta_compare_plans')}
          </Link>
        )}

        {dismissable && (
          <button
            onClick={() => dismiss(dismissKey)}
            aria-label={t('expiry_cta_dismiss')}
            className="rounded-md p-1 opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-charcoal-950"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {progressPercent !== null && (
        <div
          aria-hidden
          className={cn(
            'pointer-events-none absolute inset-x-0 -bottom-[2px] h-1 overflow-hidden',
            styles.progressTrack,
          )}
        >
          <span
            className={cn('block h-full transition-all duration-500', styles.progressFill)}
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      )}
    </div>
  );
}
